import React, { useState } from 'react'
import { Grid } from '@material-ui/core'
import Decimal from 'decimal.js'
import DecimalTextField from 'UI/TextField/DecimalTextField'
import { DECIMAL_PLACES, DECIMAL_PLACES_PERCENTAGE } from 'constants/calcConstants'
import { mathRoundingAmount, mathRoundingPercentage } from 'utils/rounding'

interface Props {
  commision: IIsoCommision
  isoFeeAmount: number | undefined
  isoId: number
  isoName: string
  disabled: boolean | undefined
  mode: SplitIsoCommisionCalcultationMode
  onChange: (id: number, commission: IIsoCommision) => {}
}

interface IIsoCommision {
  percentage: string
  amount: string
}

enum SplitIsoCommisionCalcultationMode {
  Percentage,
  Amount,
}

interface IIsoCommisionDataValidation {
  mode: SplitIsoCommisionCalcultationMode | null
  validate: (data: string) => {}
  error: string
}

const dataValidations: Array<IIsoCommisionDataValidation> = [
  {
    mode: null,
    validate: (data: string) => {
      const trimmed = data.trim()
      if (trimmed === '') return true
      const normalized = trimmed.replace(',', '.')
      return !Number.isNaN(Number(normalized))
    },
    error: 'Value must be a number.',
  },
  {
    mode: null,
    validate: (data: string) => {
      const trimmed = data.trim()
      if (trimmed === '') return true
      const value = Number(data)
      return value >= 0
    },
    error: 'Value cannot be negative.',
  },
  {
    mode: null,
    validate: (data: string) => {
      const trimmed = data.trim()
      if (trimmed === '') return true
      const value = Number(data)
      return value > 0
    },
    error: 'Value must be higher than zero.',
  },
  {
    mode: null,
    validate: (data: string) => {
      const trimmed = data.trim()
      if (trimmed === '') return true
      const value = Number(data)
      return value > 0
    },
    error: 'Value must be higher than zero.',
  },
  {
    mode: SplitIsoCommisionCalcultationMode.Percentage,
    validate: (data: string) => {
      const trimmed = data.trim()
      if (trimmed === '') return true
      const value = new Decimal(data)
      return value.times(10000).mod(1).equals(0)
    },
    error: 'Percentage should be rounded to 4 decimal places or fewer.',
  },
]

export default function SplitIsoCommissionInput({
  commision,
  disabled,
  mode = SplitIsoCommisionCalcultationMode.Amount,
  isoName,
  isoId,
  isoFeeAmount,
  onChange,
  ...props
}: Props) {
  const [error, setError] = useState<string | null>(null)

  const isError = !!error
  const value =
    mode === SplitIsoCommisionCalcultationMode.Percentage ? commision.percentage : commision.amount
  const validations = dataValidations.filter(v => v.mode === mode || v.mode === null)

  const handlePercentageChanged = (percentage: string) => {
    const perc = new Decimal(percentage || 0)
    const total = new Decimal(isoFeeAmount || 0)
    const formattedPercentage = perc.toDecimalPlaces(
      DECIMAL_PLACES_PERCENTAGE,
      Decimal.ROUND_HALF_EVEN
    )
    const calculatedAmount = formattedPercentage.dividedBy(100).times(total)
    const formattedAmount = calculatedAmount.toDecimalPlaces(
      DECIMAL_PLACES,
      Decimal.ROUND_HALF_EVEN
    )

    onChange(isoId, {
      amount: formattedAmount.toString(),
      percentage: formattedPercentage.toString(),
    })
  }

  const handleAmountChanged = (amount: string) => {
    const amt = new Decimal(amount || 0)
    const total = new Decimal(isoFeeAmount || 0)
    const formattedAmount = amt.toDecimalPlaces(DECIMAL_PLACES, Decimal.ROUND_HALF_EVEN)
    const calculatedPercentage = total.isZero()
      ? new Decimal(0)
      : formattedAmount.dividedBy(total).times(100)
    const formattedPercentage = calculatedPercentage.toDecimalPlaces(
      DECIMAL_PLACES_PERCENTAGE,
      Decimal.ROUND_HALF_EVEN
    )

    onChange(isoId, {
      amount: formattedAmount.toString(),
      percentage: formattedPercentage.toString(),
    })
  }

  const handleChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const changedValue = e.target.value

    for (const validation of validations) {
      if (!validation.validate(changedValue)) {
        setError(validation.error)
        return
      }
    }

    if (isError) {
      setError(null)
    }

    if (mode === SplitIsoCommisionCalcultationMode.Percentage) {
      onChange(isoId, { ...commision, percentage: changedValue })
    } else {
      onChange(isoId, { ...commision, amount: changedValue })
    }
  }

  const getHelperText = (): string => {
    if (isError) {
      return error!
    }

    if (mode === SplitIsoCommisionCalcultationMode.Percentage) {
      return `Equivalent Amount: $${mathRoundingAmount(+commision.amount)}`
    }

    return `Equivalent Percentage: ${mathRoundingPercentage(+commision.percentage)}%`
  }

  return (
    <Grid container spacing={2}>
      <DecimalTextField
        decimalScale={
          mode === SplitIsoCommisionCalcultationMode.Percentage
            ? DECIMAL_PLACES_PERCENTAGE
            : DECIMAL_PLACES
        }
        {...props}
        disabled={disabled}
        variant="outlined"
        placeholder={
          mode === SplitIsoCommisionCalcultationMode.Percentage ? 'Percentage' : 'Amount'
        }
        autoComplete={undefined}
        fullWidth
        InputLabelProps={{ shrink: true }}
        error={isError}
        helperText={getHelperText()}
        name={`${isoId}-${SplitIsoCommisionCalcultationMode[mode]}`}
        label={isoName?.trim()}
        key={`commission-${SplitIsoCommisionCalcultationMode[mode]}-${isoId}`}
        value={value}
        onChange={handleChanged}
        onBlur={() => {
          if (mode === SplitIsoCommisionCalcultationMode.Percentage) {
            handlePercentageChanged(value)
          } else {
            handleAmountChanged(value)
          }
        }}
      />
    </Grid>
  )
}
